import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import logo from '../assets/logo.png';

export const exportToExcel = (data, filename, sheetName = 'Sheet1', title = '') => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);
  
  // Set column widths
  const maxWidths = [];
  if (data.length > 0) {
    Object.keys(data[0]).forEach(key => {
      maxWidths.push({
        wch: Math.max(
          key.length,
          ...data.map(row => String(row[key] || '').length)
        )
      });
    });
  }
  worksheet['!cols'] = maxWidths;
  
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const exportToPDF = (data, filename, title, columns = null) => {
  const doc = new jsPDF();
  
  // Add logo
  try {
    doc.addImage(logo, 'PNG', 14, 10, 20, 20);
  } catch (e) {
    console.log('Logo not found, skipping');
  }
  
  // Add title
  doc.setFontSize(16);
  doc.setTextColor(118, 0, 188); // #7600bc
  doc.text(title, 40, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 40, 28);
  
  // Prepare table data
  let tableData = [];
  let headers = [];
  
  if (columns) {
    headers = columns.map(col => col.header);
    tableData = data.map(row => columns.map(col => row[col.dataKey]));
  } else if (data.length > 0) {
    headers = Object.keys(data[0]);
    tableData = data.map(row => Object.values(row));
  }
  
  doc.autoTable({
    head: [headers],
    body: tableData,
    startY: 35,
    theme: 'grid',
    headStyles: {
      fillColor: [118, 0, 188], // #7600bc
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [50, 50, 50]
    },
    alternateRowStyles: {
      fillColor: [245, 240, 250]
    }
  });
  
  doc.save(`${filename}.pdf`);
};

// Export payment report with study year grouping
// Gedeelde hulpfuncties voor het betalingsafschrift van één student.
const STATEMENT_PURPLE = [118, 0, 188];

const statementTypeLabel = (payment) => {
  const rawType = payment.paymentTypeKey || payment.paymentType || 'oneTime';
  const labels = {
    monthly: 'Maandelijks',
    quarterly: 'Per kwartaal',
    yearly: 'Jaarlijks',
    oneTime: 'Eenmalig',
    'one-time': 'Eenmalig',
    'school-year': 'Schooljaar',
    schoolYear: 'Schooljaar',
    enrollmentFee: 'Inschrijfgeld',
    'enrollment-fee': 'Inschrijfgeld'
  };
  return labels[rawType] || rawType;
};

const statementStatusLabel = (status) => ({
  pending: 'Open',
  partial: 'Deels betaald',
  paid: 'Betaald',
  overdue: 'Achterstallig',
  cancelled: 'Geannuleerd'
})[status] || status || '-';

const statementYearLabel = (payment, studyYears = []) => {
  if (payment.schoolYearLabel) return payment.schoolYearLabel;
  const studyYear = (studyYears || []).find((sy) => String(sy.id) === String(payment.studyYearId));
  if (studyYear) return `Jaar ${studyYear.year}`;
  return payment.schoolYear || 'Onbekend schooljaar';
};

const statementTotal = (payment) => Number((payment.totalAmount ?? payment.amount) || 0);
const statementPaid = (payment) => Number(payment.paidAmount || 0);
const statementRemaining = (payment) => Math.max(0, statementTotal(payment) - statementPaid(payment));
const statementMoney = (value) => `SRD ${Number(value || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Datums uit een datumveld staan als UTC-middernacht opgeslagen; in UTC tonen voorkomt een dag verschuiving (UTC-3).
const statementDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const isUtcMidnight = date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
  return date.toLocaleDateString('nl-NL', isUtcMidnight ? { timeZone: 'UTC' } : undefined);
};

// Aflossingen op datum, met het openstaande bedrag na elke aflossing.
const statementInstallments = (payment) => {
  let running = 0;
  return [...(payment.payments || [])]
    .sort((a, b) => new Date(a.paymentDate || 0) - new Date(b.paymentDate || 0))
    .map((entry, index) => {
      running += Number(entry.amount || 0);
      return {
        nr: index + 1,
        date: entry.paymentDate,
        amount: Number(entry.amount || 0),
        method: entry.paymentMethod || '',
        notes: entry.notes || '',
        remainingAfter: Math.max(0, statementTotal(payment) - running)
      };
    });
};

const groupStatementByYear = (payments, studyYears) => {
  const groups = {};
  payments.forEach((payment) => {
    const key = statementYearLabel(payment, studyYears);
    if (!groups[key]) groups[key] = [];
    groups[key].push(payment);
  });
  return groups;
};

export const exportPaymentReport = (payments, student, studyYears, filename) => {
  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.getHeight();

  try {
    // Logo in de juiste verhouding (1529 x 649).
    doc.addImage(logo, 'PNG', 14, 10, 40, 17);
  } catch (e) {
    console.log('Logo not found');
  }

  doc.setFontSize(16);
  doc.setTextColor(...STATEMENT_PURPLE);
  doc.text('Betalingsafschrift', 60, 21);

  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text(`Student: ${student.name}`, 14, 38);
  doc.text(`E-mail: ${student.email || '-'}`, 14, 45);
  doc.text(`Afgedrukt op: ${new Date().toLocaleDateString('nl-NL')}`, 14, 52);

  let currentY = 62;
  const ensureSpace = (needed) => {
    if (currentY + needed > pageHeight - 15) {
      doc.addPage();
      currentY = 18;
    }
  };

  const paymentsByYear = groupStatementByYear(payments, studyYears);

  Object.keys(paymentsByYear).forEach((yearKey) => {
    ensureSpace(30);
    doc.setFontSize(12);
    doc.setTextColor(...STATEMENT_PURPLE);
    doc.text(yearKey, 14, currentY);
    currentY += 4;

    paymentsByYear[yearKey].forEach((payment) => {
      doc.autoTable({
        head: [['Type', 'Totaal', 'Betaald', 'Openstaand', 'Vervaldatum', 'Status']],
        body: [[
          statementTypeLabel(payment),
          statementMoney(statementTotal(payment)),
          statementMoney(statementPaid(payment)),
          statementMoney(statementRemaining(payment)),
          statementDate(payment.dueDate),
          statementStatusLabel(payment.status)
        ]],
        startY: currentY,
        theme: 'grid',
        headStyles: { fillColor: STATEMENT_PURPLE, textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8.5 }
      });
      currentY = doc.lastAutoTable.finalY + 3;

      const installments = statementInstallments(payment);
      if (installments.length === 0) {
        ensureSpace(10);
        doc.setFontSize(8.5);
        doc.setTextColor(110);
        doc.text('Nog geen aflossingen geboekt.', 16, currentY + 4);
        currentY += 10;
      } else {
        doc.autoTable({
          head: [['Aflossing', 'Datum', 'Bedrag', 'Methode', 'Notitie', 'Openstaand na aflossing']],
          body: installments.map((item) => [
            `#${item.nr}`,
            statementDate(item.date),
            statementMoney(item.amount),
            item.method || '-',
            item.notes || '',
            statementMoney(item.remainingAfter)
          ]),
          foot: [[
            'Totaal',
            '',
            statementMoney(installments.reduce((sum, item) => sum + item.amount, 0)),
            '',
            '',
            statementMoney(statementRemaining(payment))
          ]],
          startY: currentY,
          theme: 'grid',
          margin: { left: 20 },
          headStyles: { fillColor: [237, 233, 254], textColor: [76, 29, 149], fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          footStyles: { fillColor: [245, 243, 255], textColor: [40, 40, 40], fontSize: 8, fontStyle: 'bold' },
          columnStyles: { 2: { halign: 'right' }, 5: { halign: 'right' }, 4: { cellWidth: 45 } }
        });
        currentY = doc.lastAutoTable.finalY + 8;
      }
    });

    currentY += 4;
  });

  const totalAmount = payments.reduce((sum, p) => sum + statementTotal(p), 0);
  const totalPaid = payments.reduce((sum, p) => sum + statementPaid(p), 0);
  const installmentCount = payments.reduce((sum, p) => sum + (p.payments || []).length, 0);

  ensureSpace(32);
  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text(`Totaal te betalen: ${statementMoney(totalAmount)}`, 14, currentY);
  doc.text(`Totaal betaald: ${statementMoney(totalPaid)} (${installmentCount} aflossing(en))`, 14, currentY + 8);
  doc.text(`Openstaand: ${statementMoney(Math.max(0, totalAmount - totalPaid))}`, 14, currentY + 16);

  doc.save(`${filename}.pdf`);
};

// Export payment report to Excel
export const exportPaymentReportExcel = (payments, student, studyYears, filename) => {
  const workbook = XLSX.utils.book_new();
  const totalAmount = payments.reduce((sum, p) => sum + statementTotal(p), 0);
  const totalPaid = payments.reduce((sum, p) => sum + statementPaid(p), 0);

  const summarySheet = XLSX.utils.aoa_to_sheet([
    ['Betalingsafschrift'],
    [''],
    ['Student:', student.name],
    ['E-mail:', student.email || ''],
    ['Afgedrukt op:', new Date().toLocaleDateString('nl-NL')],
    [''],
    ['Totaal te betalen (SRD):', totalAmount],
    ['Totaal betaald (SRD):', totalPaid],
    ['Openstaand (SRD):', Math.max(0, totalAmount - totalPaid)],
    ['Aantal aflossingen:', payments.reduce((sum, p) => sum + (p.payments || []).length, 0)]
  ]);
  summarySheet['!cols'] = [{ wch: 26 }, { wch: 34 }];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Overzicht');

  // Per schooljaar: elke betaling met daaronder de aflossingen en totalen.
  const paymentsByYear = groupStatementByYear(payments, studyYears);
  const usedNames = new Set(['Overzicht', 'Alle aflossingen']);
  Object.keys(paymentsByYear).forEach((yearKey) => {
    const rows = [[`Schooljaar ${yearKey}`], []];
    paymentsByYear[yearKey].forEach((payment) => {
      rows.push(['Type', 'Totaal (SRD)', 'Betaald (SRD)', 'Openstaand (SRD)', 'Vervaldatum', 'Status']);
      rows.push([
        statementTypeLabel(payment),
        statementTotal(payment),
        statementPaid(payment),
        statementRemaining(payment),
        statementDate(payment.dueDate),
        statementStatusLabel(payment.status)
      ]);
      rows.push([]);
      const installments = statementInstallments(payment);
      if (installments.length === 0) {
        rows.push(['Nog geen aflossingen geboekt.']);
      } else {
        rows.push(['Aflossing', 'Datum', 'Bedrag (SRD)', 'Methode', 'Notitie', 'Openstaand na aflossing (SRD)']);
        installments.forEach((item) => rows.push([
          item.nr,
          statementDate(item.date),
          item.amount,
          item.method,
          item.notes,
          item.remainingAfter
        ]));
        rows.push(['Totaal afgelost', '', installments.reduce((sum, item) => sum + item.amount, 0), '', '', statementRemaining(payment)]);
      }
      rows.push([], []);
    });
    const yearTotal = paymentsByYear[yearKey].reduce((sum, p) => sum + statementTotal(p), 0);
    const yearPaid = paymentsByYear[yearKey].reduce((sum, p) => sum + statementPaid(p), 0);
    rows.push(['Subtotaal schooljaar', yearTotal, yearPaid, Math.max(0, yearTotal - yearPaid)]);

    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 30 }, { wch: 26 }];
    // Bladnamen: max. 31 tekens, uniek en zonder ongeldige tekens.
    let sheetName = String(yearKey).replace(/[\\/?*[\]:]/g, '-').slice(0, 31) || 'Schooljaar';
    let counter = 2;
    while (usedNames.has(sheetName)) sheetName = `${sheetName.slice(0, 28)} ${counter++}`;
    usedNames.add(sheetName);
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
  });

  // Alle aflossingen in één lijst, handig om te filteren.
  const allInstallments = [];
  payments.forEach((payment) => {
    statementInstallments(payment).forEach((item) => allInstallments.push({
      Schooljaar: statementYearLabel(payment, studyYears),
      Type: statementTypeLabel(payment),
      Nr: item.nr,
      Datum: statementDate(item.date),
      'Bedrag (SRD)': item.amount,
      Methode: item.method,
      Notitie: item.notes,
      'Openstaand na aflossing (SRD)': item.remainingAfter
    }));
  });
  const installmentSheet = XLSX.utils.json_to_sheet(allInstallments.length ? allInstallments : [{ Melding: 'Nog geen aflossingen geboekt' }]);
  XLSX.utils.book_append_sheet(workbook, installmentSheet, 'Alle aflossingen');

  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export course scores report
export const exportCourseScoresReport = (course, students, grades, filename) => {
  const doc = new jsPDF();

  try {
    doc.addImage(logo, 'PNG', 14, 10, 20, 20);
  } catch (e) {}

  const studyYearLabel = (() => {
    const rawYear = course?.studyYear?.name || course?.studyYear?.year || course?.yearLabel || course?.studyYearName || 'LEERJAAR 1';
    return String(rawYear || '').trim() || 'LEERJAAR 1';
  })();

  const formatBirthDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('nl-NL');
  };

  doc.setFontSize(16);
  doc.setTextColor(118, 0, 188);
  doc.text('Netherlands Business Academy', 40, 20);

  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text(studyYearLabel.toUpperCase(), 14, 35);
  doc.text(`Vak: ${course?.name || 'Cursus'} (${course?.code || '-'})`, 14, 43);
  doc.text(`Gegenereerd: ${new Date().toLocaleDateString('nl-NL')}`, 14, 51);

  students.forEach((student, index) => {
    if (index > 0) {
      doc.addPage();
    }

    const studentGrades = (grades || []).filter(
      (g) => String(g.studentId) === String(student.id || student._id) && String(g.courseId) === String(course.id || course._id)
    );

    const studentAverage = studentGrades.length > 0
      ? studentGrades.reduce((sum, g) => sum + Number(g.grade || 0), 0) / studentGrades.length
      : 0;

    const studentCode = student.studentCode || student.studentNumber || student.code || '-';
    const birthDate = formatBirthDate(student.dateOfBirth || student.birthDate);

    const headerY = 20;
    doc.setFontSize(12);
    doc.setTextColor(118, 0, 188);
    doc.text('Naam:', 14, headerY + 18);
    doc.text('Studentcode:', 14, headerY + 26);
    doc.text('Geboorte datum:', 14, headerY + 34);
    doc.text('Totaal te behalen ECTS:', 100, headerY + 18);
    doc.text('ECTS Behaald:', 100, headerY + 26);
    doc.text('Voortgang studie:', 100, headerY + 34);

    doc.setTextColor(50);
    doc.text(String(student.name || '-'), 42, headerY + 18);
    doc.text(String(studentCode), 42, headerY + 26);
    doc.text(String(birthDate), 42, headerY + 34);
    doc.text(String(Number(course?.ects || 0)), 155, headerY + 18);
    doc.text(String(studentGrades.length > 0 ? Number((studentAverage / 10) * Number(course?.ects || 0)).toFixed(1) : '0.0'), 155, headerY + 26);
    doc.text(String(studentGrades.length > 0 ? `${((studentAverage / 10) * 100).toFixed(0)}%` : '0%'), 155, headerY + 34);

    const rows = studentGrades.length > 0
      ? studentGrades.map((grade) => [
          String(grade.examPointKey || grade.examType || 'OND'),
          String(grade.notes || grade.examType || 'Assessment'),
          String(course?.ects || 0),
          String(grade.examType || 'Toets'),
          String(Number(grade.grade || 0).toFixed(1)),
          String(Number(grade.grade || 0).toFixed(1))
        ])
      : [[ '-', course?.name || 'Cursus', String(course?.ects || 0), '-', '-', '-' ]];

    doc.autoTable({
      head: [['CODE', 'MODULE NAAM', 'ECTS', 'TOETSING', 'CIJFER', 'EINDCIJFER']],
      body: rows,
      startY: 62,
      theme: 'grid',
      headStyles: {
        fillColor: [118, 0, 188],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [50, 50, 50]
      },
      alternateRowStyles: {
        fillColor: [245, 240, 250]
      },
      margin: { left: 14, right: 14 }
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(10);
    doc.setTextColor(50);
    doc.text(`Gemiddeld: ${Number.isFinite(studentAverage) ? Number(studentAverage).toFixed(1) : '0.0'}`, 14, finalY);
    doc.text(`Status: ${studentAverage >= 5.5 ? 'Voldoende' : 'Onvoldoende'}`, 120, finalY);
  });

  doc.save(`${filename}.pdf`);
};

export const exportCourseScoresExcel = (course, students, grades, filename) => {
  const rows = [];
  const studyYearLabel = course?.studyYear?.name || course?.yearLabel || 'LEERJAAR 1';

  students.forEach((student) => {
    const studentGrades = (grades || []).filter(
      (g) => String(g.studentId) === String(student.id || student._id) && String(g.courseId) === String(course.id || course._id)
    );

    const studentCode = student.studentCode || student.studentNumber || student.code || '-';
    const birthDate = student.dateOfBirth || student.birthDate || '-';
    const average = studentGrades.length > 0
      ? studentGrades.reduce((sum, g) => sum + Number(g.grade || 0), 0) / studentGrades.length
      : 0;

    rows.push({
      'LEERJAAR': studyYearLabel,
      'Naam': student.name || '-',
      'Studentcode': studentCode,
      'Geboorte datum': birthDate,
      'CODE': course?.code || '-',
      'MODULE NAAM': course?.name || '-',
      'ECTS': course?.ects || 0,
      'TOETSING': studentGrades.length > 0 ? (studentGrades[0]?.examType || 'Toets') : '-',
      'CIJFER': studentGrades.length > 0 ? Number(studentGrades[0].grade || 0).toFixed(1) : '0.0',
      'EINDCIJFER': Number.isFinite(average) ? Number(average).toFixed(1) : '0.0',
      'Status': average >= 5.5 ? 'Voldoende' : 'Onvoldoende'
    });
  });

  exportToExcel(rows.length > 0 ? rows : [{
    'LEERJAAR': studyYearLabel,
    'Naam': '-',
    'Studentcode': '-',
    'Geboorte datum': '-',
    'CODE': course?.code || '-',
    'MODULE NAAM': course?.name || '-',
    'ECTS': course?.ects || 0,
    'TOETSING': '-',
    'CIJFER': '0.0',
    'EINDCIJFER': '0.0',
    'Status': 'Onvoldoende'
  }], filename, course?.name || 'Cijferlijst');
};
